import { z } from 'zod';
import { DocId, IsoTimestamp } from '../common.js';
/**
 * Cross-document staleness audit suggestions.
 *
 * LLM-driven companion to the manual stale-term scanner. A periodic
 * audit script samples chunks from `kennisbank_ring1`, asks Gemini
 * Flash to flag concrete facts (URLs, emails, brand names, product
 * names, people, version numbers) with a reasonable chance of being
 * outdated, and writes the candidates here for an admin to review.
 *
 * Top-level collection: `canonical_fact_suggestions/{id}`. Cross-tenant,
 * kb_admin RBAC. Mirrors the canonical-facts shared-corpus
 * exception in CLAUDE.md — admins curate from `/admin/documents`.
 *
 * Lifecycle:
 *   pending   — written by the audit script, awaiting review
 *   accepted  — admin promoted the suggestion to a canonical fact
 *   dismissed — admin rejected it (false positive / already current)
 */
export const StaleCategory = z.enum([
    'url',
    'email',
    'brand',
    'product_name',
    'person',
    'version',
    'other',
]);
export const SuggestionReviewStatus = z.enum(['pending', 'accepted', 'dismissed']);
export const CanonicalFactSuggestion = z.object({
    id: DocId,
    staleTerm: z.string().min(1).max(500),
    category: StaleCategory,
    rationale: z.string().min(1).max(1000),
    confidence: z.number().min(0).max(1),
    sourceChunkId: z.string().min(1).max(1500),
    sourceFile: z.string().min(1).max(500),
    sourceDocumentTitle: z.string().max(500).default(''),
    excerpt: z.string().max(1000),
    detectedAt: IsoTimestamp,
    reviewStatus: SuggestionReviewStatus.default('pending'),
    reviewedAt: IsoTimestamp.nullable().default(null),
    reviewedBy: z.string().max(200).nullable().default(null),
});
export const AcceptSuggestionInput = z.object({
    statement: z
        .string()
        .min(8, 'A canonical fact needs at least 8 characters.')
        .max(1000, 'Keep canonical facts under 1000 characters — they ride in every prompt.'),
});
export const DismissSuggestionInput = z.object({
    reason: z.string().max(500).optional(),
});
//# sourceMappingURL=canonical-fact-suggestion.js.map